import gsap, { DrawSVGPlugin } from 'gsap/all'
import { useEffect } from 'react'
import Marquee from 'react-fast-marquee'
import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'
import { useNavigate } from 'react-router'
import 'swiper/css'

import { useGSAP } from '@gsap/react'

import Icon from '@/components/Icon'
import TouchIndicator from '@/components/TouchIndicator'
import useLayout from '@/layout/useLayout'
import { PrinterState } from '@/lib/printerState'
import useTerminal from '@/lib/useTerminal'
import View from '@/layout/View'

import useCheckout from '../Checkout/hooks/useCheckout'
import { IntroIndicator, IntroLogo, IntroSlider, IntroSliderImage, IntroTitle, IntroToggler, IntroWrapper } from './Intro.styles'

gsap.registerPlugin(useGSAP, DrawSVGPlugin)

const Intro = () => {
  const { t } = useTranslation()
  const { reset, setPrinterStatus } = useCheckout()
  const navigate = useNavigate()
  const { setAccessibilityMode, accessibilityMode } = useLayout()
  const { getPrinterStatus } = useTerminal()

  const gallery = ['/img/gallery01.jpg', '/img/gallery02.jpg', '/img/gallery03.jpg']

  useEffect(() => {
    reset()
  }, [])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return
    }

    let active = true
    let lastState: 'ok' | 'disconnected' | 'coverOpen' | 'paperEmpty' | 'notReady' | 'error' | null = null
    const logTransition = (next: typeof lastState, detail?: string) => {
      if (lastState === next) return
      console.log(`[intro] printer state: ${lastState ?? 'initial'} -> ${next}${detail ? ` (${detail})` : ''}`)
      lastState = next
    }

    const checkPrinterStatus = () => {
      const t0 = Date.now()
      getPrinterStatus()
        .then(status => {
          if (!active) return

          const elapsed = Date.now() - t0
          if (status.ready) {
            if (status.paperNearEmpty) {
              setPrinterStatus(PrinterState.PAPER_NEAR_EMPTY)
            } else {
              setPrinterStatus(PrinterState.READY)
            }
            logTransition('ok', `elapsed=${elapsed}ms`)
            return
          }

          if (!status.connected) {
            setPrinterStatus(PrinterState.OFFLINE)
            logTransition('disconnected', `elapsed=${elapsed}ms`)
            return
          }

          if (status.coverOpen) {
            setPrinterStatus(PrinterState.COVER_OPEN)
            logTransition('coverOpen', `elapsed=${elapsed}ms`)
            return
          }

          if (status.paperEmpty) {
            setPrinterStatus(PrinterState.NO_PAPER)
            logTransition('paperEmpty', `elapsed=${elapsed}ms`)
            return
          }

          setPrinterStatus(PrinterState.UNKNOWN_ERROR)
          logTransition('notReady', `hasError=${status.hasError}, elapsed=${elapsed}ms`)
        })
        .catch(error => {
          if (!active) return
          const elapsed = Date.now() - t0
          logTransition('error', `elapsed=${elapsed}ms, error=${error instanceof Error ? error.message : String(error)}`)
          setPrinterStatus(PrinterState.UNKNOWN_ERROR)
        })
    }

    checkPrinterStatus()
    const interval = setInterval(checkPrinterStatus, 10_000)

    return () => {
      active = false
      clearInterval(interval)
    }
  }, [getPrinterStatus, setPrinterStatus])

  return (
    <View>
      <IntroWrapper onClick={() => navigate('/tickets')}>
        <IntroLogo>
          <SVG src={require('@/assets/img/logo.svg').default}></SVG>
        </IntroLogo>
        <IntroSlider>
          <Marquee>
            {gallery?.map((media: string, index: number) => (
              <IntroSliderImage key={index}>
                <img src={media} alt="..." />
              </IntroSliderImage>
            ))}
          </Marquee>
        </IntroSlider>
        <IntroTitle>{t('buyTicket')}</IntroTitle>
        <IntroIndicator>
          <TouchIndicator label={t('touchToStart')} />
        </IntroIndicator>
      </IntroWrapper>

      <IntroToggler onClick={() => setAccessibilityMode(!accessibilityMode)}>
        <span>{t('accessibilityMode')}</span>
        <Icon name="accessibility" />
      </IntroToggler>

      {/* <IntroScheduler /> */}
    </View>
  )
}

export default Intro
